/**
 * Super-admin: tank stock race under-deduction — sales litres vs nozzle_closing tank logs.
 * Open vs Fixed tabs keep corrected cases (e.g. Sundale) out of the active worklist.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import {
  fetchTankStockMismatchDetail,
  fetchTankStockMismatches,
  fixTankStockMismatch,
  friendlyFetchError,
} from './adminApi';
import { formatReportDate } from '../../../utils/reportFormat';
import './admin-dashboard.css';

function formatLitres(value) {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const AdminTankStockErrors = (props) => {
  const history = useHistory();
  const { pumpId } = useParams();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) {
          setReady(true);
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }, [history]);

  if (!ready) {
    return (
      <div className="main-container page-wrap admin-platform">
        <div className="d-md-flex">
          <Sidebar_Progress props={props} progress={36} />
          <div className="content-area">
            <LoginHeaderOperational props={props} page_title="Tank stock errors" />
            <div className="container pb-3 mt-3">
              <p className="text-muted">Checking access…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return pumpId
    ? <TankStockDetail props={props} pumpId={pumpId} />
    : <TankStockList props={props} />;
};

const TankStockList = ({ props }) => {
  const [pumps, setPumps] = useState([]);
  const [meta, setMeta] = useState({
    pumps_with_open: 0,
    pumps_with_fixed: 0,
    open_errors: 0,
    fixed_errors: 0,
    total_errors: 0,
    checked_pumps: 0,
    generated_at: '',
  });
  const [tab, setTab] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function load(refresh = false, status = tab) {
    setLoading(true);
    setError('');
    fetchTankStockMismatches({ onlyErrors: true, refresh, status })
      .then((data) => {
        setPumps(data.pumps || []);
        setMeta({
          pumps_with_open: data.pumps_with_open || 0,
          pumps_with_fixed: data.pumps_with_fixed || 0,
          open_errors: data.open_errors || 0,
          fixed_errors: data.fixed_errors || 0,
          total_errors: data.total_errors || 0,
          checked_pumps: data.checked_pumps || 0,
          generated_at: data.generated_at || '',
        });
      })
      .catch((err) => setError(friendlyFetchError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(false, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const isFixedTab = tab === 'fixed';

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={36} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Tank stock errors" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <header className="admin-dash-home__header mt-3">
              <div>
                <h2 className="admin-dash-home__title">Tank stock race errors</h2>
                <p className="admin-dash-home__subtitle">
                  Closed nozzle sales litres vs net nozzle_closing tank deductions.
                  Positive remaining gap = sale not deducted from tank (concurrent close race).
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => load(true, tab)}
                disabled={loading}
              >
                {loading ? 'Scanning…' : 'Refresh scan'}
              </button>
            </header>

            <div className="admin-dash-kpi-row mb-3">
              <div className="admin-dash-kpi-card admin-dash-kpi-card--danger">
                <p className="admin-dash-kpi-card__value">{meta.pumps_with_open}</p>
                <p className="admin-dash-kpi-card__label">Pumps with open gaps</p>
              </div>
              <div className="admin-dash-kpi-card admin-dash-kpi-card--warning">
                <p className="admin-dash-kpi-card__value">{meta.open_errors}</p>
                <p className="admin-dash-kpi-card__label">Open gaps</p>
              </div>
              <div className="admin-dash-kpi-card admin-dash-kpi-card--default">
                <p className="admin-dash-kpi-card__value">{meta.pumps_with_fixed}</p>
                <p className="admin-dash-kpi-card__label">Pumps fully fixed</p>
              </div>
              <div className="admin-dash-kpi-card admin-dash-kpi-card--default">
                <p className="admin-dash-kpi-card__value">{meta.fixed_errors}</p>
                <p className="admin-dash-kpi-card__label">Fixed cases</p>
              </div>
            </div>

            <div className="admin-panel admin-dash-panel">
              <div className="admin-dash-panel__head d-flex flex-wrap align-items-center gap-2">
                <div className="btn-group" role="group" aria-label="Open or fixed">
                  <button
                    type="button"
                    className={`btn btn-sm ${tab === 'open' ? 'btn-danger' : 'btn-outline-secondary'}`}
                    onClick={() => setTab('open')}
                    disabled={loading}
                  >
                    Open ({meta.open_errors})
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${tab === 'fixed' ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => setTab('fixed')}
                    disabled={loading}
                  >
                    Fixed ({meta.fixed_errors})
                  </button>
                </div>
                <h5 className="mb-0 ms-auto">{isFixedTab ? 'Fully fixed pumps' : 'Pumps needing fix'}</h5>
              </div>
              {meta.generated_at ? (
                <p className="admin-dash-panel__meta">Last scan {meta.generated_at}</p>
              ) : null}
              <p className="small text-muted">
                {isFixedTab
                  ? 'Cases already offset by stock gain/loss or a prior race fix. No action needed.'
                  : 'Only pumps with remaining under-deduction. Fixed pumps move to the Fixed tab.'}
              </p>

              {error ? <p className="text-danger">{error}</p> : null}
              {loading ? <p className="text-muted">Scanning tank stock across pumps…</p> : null}

              {!loading && !error ? (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Pump</th>
                        <th>{isFixedTab ? 'Fixed cases' : 'Open'}</th>
                        <th>{isFixedTab ? 'Fixed litres' : 'Open litres'}</th>
                        <th>Latest</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {pumps.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-muted">
                            {isFixedTab
                              ? 'No fully fixed pumps yet.'
                              : 'No open tank stock race gaps.'}
                          </td>
                        </tr>
                      ) : null}
                      {pumps.map((row) => (
                        <tr key={row.pump_id}>
                          <td>
                            <strong>{row.pump_name}</strong>
                            <div className="small text-muted">#{row.pump_id}</div>
                          </td>
                          <td>
                            {isFixedTab ? row.already_corrected_count : row.open_count}
                          </td>
                          <td>
                            {formatLitres(isFixedTab ? row.fixed_litres : row.open_litres)}
                          </td>
                          <td>{row.latest_op_date || '—'}</td>
                          <td>
                            <Link
                              to={`/admin-tank-stock-errors/${row.pump_id}?tab=${tab}`}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TankStockDetail = ({ props, pumpId }) => {
  const history = useHistory();
  const initialTab = new URLSearchParams(history.location?.search || '').get('tab') === 'fixed'
    ? 'fixed'
    : 'open';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [caseTab, setCaseTab] = useState(initialTab);

  function load() {
    setLoading(true);
    setError('');
    fetchTankStockMismatchDetail(pumpId)
      .then((data) => {
        setDetail(data);
        const openCount = data.open_count || 0;
        const fixedCount = data.already_corrected_count || 0;
        if (initialTab === 'fixed' && fixedCount > 0) {
          setCaseTab('fixed');
        } else if (openCount === 0 && fixedCount > 0) {
          setCaseTab('fixed');
        } else if (openCount > 0) {
          setCaseTab('open');
        }
      })
      .catch((err) => setError(friendlyFetchError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pumpId]);

  async function runFix(caseRow, previewOnly) {
    const key = `${caseRow.tank_id}:${caseRow.op_date}:${previewOnly ? 'p' : 'a'}`;
    setBusyKey(key);
    setActionMsg('');
    try {
      const reason = previewOnly
        ? 'Preview tank stock race under-deduction fix'
        : `Super-admin fix: deduct ${formatLitres(caseRow.remaining_gap)} L race under-deduction on ${caseRow.tank_name} / ${caseRow.op_date}`;
      const result = await fixTankStockMismatch(pumpId, {
        tankId: caseRow.tank_id,
        opDate: caseRow.op_date,
        reason,
        previewOnly,
      });
      setActionMsg(result.message || (previewOnly ? 'Preview ready' : 'Fix applied'));
      if (!previewOnly && result.status) {
        load();
        setCaseTab('fixed');
      }
    } catch (err) {
      setActionMsg(friendlyFetchError(err));
    } finally {
      setBusyKey('');
    }
  }

  const cases = detail?.cases || [];
  const visibleCases = useMemo(() => {
    if (caseTab === 'fixed') {
      return cases.filter((c) => c.fix_status === 'already_corrected');
    }
    return cases.filter((c) => c.fix_status === 'open');
  }, [cases, caseTab]);

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={36} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Tank stock error detail" />
          <div className="container pb-3 mt-3">
            <Link to={`/admin-tank-stock-errors`} className="small">← All tank stock errors</Link>

            {loading ? <p className="mt-3 text-muted">Loading…</p> : null}
            {error ? <p className="mt-3 alert alert-danger py-2">{error}</p> : null}
            {actionMsg ? (
              <p className={`mt-3 alert py-2 ${/fail|error|cannot|nothing to apply/i.test(actionMsg) && !/already corrected/i.test(actionMsg) ? 'alert-danger' : 'alert-success'}`}>
                {actionMsg}
              </p>
            ) : null}

            {detail && !loading ? (
              <>
                <header className="admin-dash-home__header mt-3">
                  <div>
                    <h2 className="admin-dash-home__title">{detail.pump_name || `Pump #${pumpId}`}</h2>
                    <p className="admin-dash-home__subtitle">
                      {detail.open_count || 0} open · {detail.already_corrected_count || 0} fixed
                    </p>
                  </div>
                </header>

                <p className="text-muted small">{detail.explanation}</p>

                <div className="admin-panel admin-dash-panel">
                  <div className="btn-group mb-3" role="group" aria-label="Case status">
                    <button
                      type="button"
                      className={`btn btn-sm ${caseTab === 'open' ? 'btn-danger' : 'btn-outline-secondary'}`}
                      onClick={() => setCaseTab('open')}
                    >
                      Open ({detail.open_count || 0})
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${caseTab === 'fixed' ? 'btn-success' : 'btn-outline-secondary'}`}
                      onClick={() => setCaseTab('fixed')}
                    >
                      Fixed ({detail.already_corrected_count || 0})
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Tank</th>
                          <th>Sold L</th>
                          <th>NC delta</th>
                          <th>Stock adj</th>
                          <th>{caseTab === 'fixed' ? 'Was gap' : 'Remaining'}</th>
                          <th>Confidence</th>
                          <th>Status</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleCases.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-muted">
                              {caseTab === 'fixed'
                                ? 'No fixed cases for this pump.'
                                : 'No open cases — check the Fixed tab.'}
                            </td>
                          </tr>
                        ) : null}
                        {visibleCases.map((row) => {
                          const previewKey = `${row.tank_id}:${row.op_date}:p`;
                          const applyKey = `${row.tank_id}:${row.op_date}:a`;
                          return (
                            <tr key={`${row.tank_id}-${row.op_date}`}>
                              <td>{formatReportDate(row.op_date)}</td>
                              <td>
                                {row.tank_name}
                                <div className="small text-muted">#{row.tank_id}</div>
                              </td>
                              <td>{formatLitres(row.sold_litres)}</td>
                              <td>{formatLitres(row.nc_delta)}</td>
                              <td>{formatLitres(row.stock_adj_qty)}</td>
                              <td>
                                <strong>
                                  {formatLitres(
                                    caseTab === 'fixed' ? row.under_deducted : row.remaining_gap
                                  )}
                                </strong>
                              </td>
                              <td>
                                <span className={row.confidence === 'high' ? 'text-danger' : 'text-warning'}>
                                  {row.confidence}
                                </span>
                                {row.positive_moves > 0 ? (
                                  <div className="small text-muted">{row.positive_moves} +ve log move(s)</div>
                                ) : null}
                              </td>
                              <td>{row.fix_status === 'open' ? 'Open' : 'Fixed'}</td>
                              <td className="text-nowrap">
                                {row.fixable ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-1"
                                      disabled={!!busyKey}
                                      onClick={() => runFix(row, true)}
                                    >
                                      {busyKey === previewKey ? '…' : 'Preview'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      disabled={!!busyKey}
                                      onClick={() => {
                                        if (window.confirm(
                                          `Deduct ${formatLitres(row.remaining_gap)} L from ${row.tank_name} on ${formatReportDate(row.op_date)}?`
                                        )) {
                                          runFix(row, false);
                                        }
                                      }}
                                    >
                                      {busyKey === applyKey ? '…' : 'Fix'}
                                    </button>
                                  </>
                                ) : (
                                  <span className="small text-success">Resolved</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTankStockErrors;
